import { useState } from 'react';
import { Modal, Button, Alert, Spinner } from 'react-bootstrap';
import { CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import axios from 'axios';
import { WASTE_PLAN_LIMITS, PPWR_PLAN_LIMITS, priceWithVat, VAT_RATE } from '../../config/plans';
import { getAuthHeader } from '../../utils/tools';

const cardElementOptions = {
    hidePostalCode: true,
    style: {
        base: {
            fontSize: '15px',
            color: '#0B1157',
            fontFamily: '"Open Sans", sans-serif',
            '::placeholder': { color: '#718094' },
        },
        invalid: { color: '#dc3545' },
    },
};

const formatEur = (cents) => `${(cents / 100).toFixed(2).replace('.', ',')} €`;

const CheckoutModal = ({ show, onHide, plan, module = 'waste', onSuccess }) => {
    const stripe = useStripe();
    const elements = useElements();

    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(false);

    const planLimits = module === 'ppwr' ? PPWR_PLAN_LIMITS : WASTE_PLAN_LIMITS;
    const planData = planLimits[plan];

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!stripe || !elements) return;

        setLoading(true);
        setError(null);

        try {
            const { data } = await axios.post(
                '/api/payment/create-payment-intent',
                { plan, module },
                getAuthHeader()
            );

            const cardElement = elements.getElement(CardElement);

            const { error: stripeError, paymentIntent } = await stripe.confirmCardPayment(
                data.clientSecret,
                { payment_method: { card: cardElement } }
            );

            if (stripeError) {
                setError(stripeError.message);
                return;
            }

            if (paymentIntent.status === 'succeeded') {
                await axios.post(
                    '/api/payment/activate',
                    { paymentIntentId: paymentIntent.id, module },
                    getAuthHeader()
                );
                onSuccess();
            }
        } catch (err) {
            setError(err.response?.data?.message ?? 'Nastala chyba. Skúste znova.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <Modal show={show} onHide={onHide} centered>
            <Modal.Header closeButton style={{ borderBottom: '1px solid #EDF2F7' }}>
                <Modal.Title style={{ color: '#0B1157', fontSize: '18px', fontWeight: 700 }}>
                    Upgrade na plán {planData?.label}
                </Modal.Title>
            </Modal.Header>

            <form onSubmit={handleSubmit}>
                <Modal.Body className="p-4">
                    <div
                        className="p-3 mb-4 rounded"
                        style={{ backgroundColor: '#F4F5FE' }}
                    >
                        <div className="d-flex justify-content-between align-items-center">
                            <span style={{ color: '#718094', fontSize: '14px' }}>Ročný poplatok bez DPH</span>
                            <span style={{ color: '#718094', fontSize: '14px' }}>{formatEur(planData?.price)}</span>
                        </div>
                        <div className="d-flex justify-content-between align-items-center mt-1">
                            <span style={{ color: '#718094', fontSize: '14px' }}>DPH ({Math.round(VAT_RATE * 100)} %)</span>
                            <span style={{ color: '#718094', fontSize: '14px' }}>{formatEur(priceWithVat(planData?.price) - planData?.price)}</span>
                        </div>
                        <hr style={{ margin: '8px 0', borderColor: '#EDF2F7' }} />
                        <div className="d-flex justify-content-between align-items-center">
                            <span style={{ color: '#0B1157', fontWeight: 600 }}>Celkom</span>
                            <span style={{ color: '#0B1157', fontWeight: 700, fontSize: '18px' }}>
                                {formatEur(priceWithVat(planData?.price))}
                            </span>
                        </div>
                    </div>

                    <div
                        className="p-3 rounded"
                        style={{ border: '1px solid #EDF2F7', backgroundColor: '#fff' }}
                    >
                        <CardElement options={cardElementOptions} />
                    </div>

                    {error && (
                        <Alert variant="danger" className="mt-3 mb-0" style={{ fontSize: '14px' }}>
                            {error}
                        </Alert>
                    )}
                </Modal.Body>

                <Modal.Footer style={{ borderTop: '1px solid #EDF2F7' }}>
                    <Button
                        variant="link"
                        onClick={onHide}
                        disabled={loading}
                        style={{ color: '#718094', textDecoration: 'none' }}
                    >
                        Zrušiť
                    </Button>
                    <Button
                        type="submit"
                        className="btn-custom-small px-4"
                        disabled={!stripe || loading}
                    >
                        {loading ? (
                            <>
                                <Spinner animation="border" size="sm" className="me-2" />
                                Spracúvam...
                            </>
                        ) : (
                            `Zaplatiť ${formatEur(priceWithVat(planData?.price))}`
                        )}
                    </Button>
                </Modal.Footer>
            </form>
        </Modal>
    );
};

export default CheckoutModal;
